/**
 * `ApiCtx` + `useApi()` hook. Views consume the typed `SillApi` bag through
 * the hook; the mock implementation is wired in at the provider boundary in
 * `src/app/providers.tsx`. When `services/api` lands the provider swaps in
 * the HTTP client; views stay untouched.
 */

import { createContext, useContext } from 'react';
import type { ReactNode } from 'react';

import type { SillApi } from './contract.js';

const ApiCtx = createContext<SillApi | null>(null);

export interface ApiProviderProps {
  api: SillApi;
  children: ReactNode;
}

export function ApiProvider({ api, children }: ApiProviderProps): JSX.Element {
  return <ApiCtx.Provider value={api}>{children}</ApiCtx.Provider>;
}

export function useApi(): SillApi {
  const ctx = useContext(ApiCtx);
  if (!ctx) {
    throw new Error('useApi() called outside <ApiProvider>');
  }
  return ctx;
}

/**
 * Provider-optional variant — returns null outside `<ApiProvider>` instead
 * of throwing. For views whose api use is progressive enhancement (e.g. the
 * Resolution detail's settled-evidence fetch) and whose tests render
 * without the provider. Mirrors `useOptionalHttp`.
 */
export function useOptionalApi(): SillApi | null {
  return useContext(ApiCtx);
}
